import type {ReactElement} from 'react';
import {useState} from 'react';
import {Button, Size, Type} from '@Components/button/button';
import {Text, TextSize} from '@Components/text/text';
import {useAppSelector} from '@/hooks';
import {useAiChatState} from '@AiChat/providers/ai-chat.context';
import {useAiChatMessage} from '@Components/ai-chat-window/providers/ai-chat-message.context';
import {sendPrompt} from '@Components/ai-chat-window/lib/ai-chat-prompt.library';
import {assertNever} from '@Utils/assert-never.util';
import type {DraftApproveButtonProps, DraftApproveChoice, DraftSecondaryChoice} from './draft-approve-button.types';
import {DraftButtonKind} from './draft-approve-button.types';
import {approvalRequestOf, approveButtonTypeOf, getDraftApproval, getDraftApprovalButtons, shownLabelOf} from './draft-approve-button.library';
import styles from './draft-approve-button.module.scss';

export function DraftApproveButton(props: DraftApproveButtonProps): ReactElement | null {
  const {chatId, messageId, sendReply} = useAiChatMessage();
  const {promptBoxId} = useAiChatState();
  const chat = useAppSelector((state) => state.aiChatWindow[chatId]);
  const [sendingLabel, setSendingLabel] = useState<string | null>(null);

  if (!chat?.hasWriteAccess) {
    return null;
  }

  const draftApproval = getDraftApproval(chat.messages, messageId);
  if (!draftApproval.hasApproveButton) {
    return null;
  }

  const buttons = getDraftApprovalButtons(props, draftApproval);
  const isDisabled = chat.isStreaming || sendingLabel !== null;

  const approveDraft = async (choice: DraftApproveChoice): Promise<void> => {
    setSendingLabel(choice.label);
    try {
      await sendReply(choice.label, approvalRequestOf(messageId, choice));
    } finally {
      setSendingLabel(null);
    }
  };

  const pressButton = (choice: DraftSecondaryChoice): void => {
    switch (choice.kind) {
      case DraftButtonKind.APPROVE:
        void approveDraft(choice);
        return;
      case DraftButtonKind.MESSAGE:
        sendPrompt(promptBoxId, choice.message);
        return;
      default:
        assertNever(choice);
    }
  };

  const renderButton = (choice: DraftSecondaryChoice, type: Type): ReactElement => {
    return (
      <Button
        text={shownLabelOf(choice)}
        type={type}
        size={Size.XSMALL}
        customClasses="radius-8"
        textClasses="_text-transform-none"
        isLoading={sendingLabel === choice.label}
        disabled={isDisabled}
        onClick={(): void => {
          pressButton(choice);
        }}
      />
    );
  };

  const renderSecondaryButton = (): ReactElement | null => {
    if (buttons.secondary === null) {
      return null;
    }

    return renderButton(buttons.secondary, Type.SECONDARY);
  };

  const renderApprovesAllNote = (): ReactElement | null => {
    if (draftApproval.approvedTogetherCount < 2) {
      return null;
    }

    return <Text val={window.i18next.t('pmwjs_ai_chat_draft_approves_all_drafts', {count: draftApproval.approvedTogetherCount})} size={TextSize.SM} className={styles.approvesAllNote} />;
  };

  return (
    <div className={styles.approval}>
      <div className={styles.approvalButtons}>
        {renderButton(buttons.primary, approveButtonTypeOf(buttons))}
        {renderSecondaryButton()}
      </div>
      {renderApprovesAllNote()}
    </div>
  );
}
